import { useEffect, useMemo, useState } from 'react'
import { Modal as AntModal, Select, Spin, message } from 'antd'
import { Button } from '@/components/ui'
import { api } from '@/lib/apiClient'

type SourceType = 'manual' | 'automated'

type SourceEventOption = {
  id: string
  slug: string
  name: string
}

type SourceTemplateOption = {
  sourceType: SourceType
  sourceId: string
  label: string
  subjectLine: string
  updatedAt?: string
}

type SourceEventsResponse = {
  events?: SourceEventOption[]
}

type SourceTemplatesResponse = {
  templates?: SourceTemplateOption[]
}

type SourceTemplateResponse = {
  sourceType: SourceType
  sourceId: string
  subjectLine: string
  htmlContent: string
  designJson?: Record<string, unknown>
}

export type ImportedEmailContent = {
  sourceEventId: string
  sourceEventName: string
  sourceType: SourceType
  sourceId: string
  sourceLabel: string
  subjectLine: string
  htmlContent: string
  designJson: Record<string, unknown>
}

interface LoadEmailContentModalProps {
  open: boolean
  eventSlug: string
  onClose: () => void
  onApply: (content: ImportedEmailContent) => void
}

const toTemplateKey = (template: SourceTemplateOption) => `${template.sourceType}:${template.sourceId}`

const LoadEmailContentModal = ({ open, eventSlug, onClose, onApply }: LoadEmailContentModalProps) => {
  const [sourceEvents, setSourceEvents] = useState<SourceEventOption[]>([])
  const [sourceTemplates, setSourceTemplates] = useState<SourceTemplateOption[]>([])
  const [selectedEventId, setSelectedEventId] = useState<string>()
  const [selectedTemplateKey, setSelectedTemplateKey] = useState<string>()
  const [loadingEvents, setLoadingEvents] = useState(false)
  const [loadingTemplates, setLoadingTemplates] = useState(false)
  const [loadingApply, setLoadingApply] = useState(false)

  const selectedTemplate = useMemo(
    () => sourceTemplates.find((template) => toTemplateKey(template) === selectedTemplateKey),
    [sourceTemplates, selectedTemplateKey]
  )

  const selectedEvent = useMemo(
    () => sourceEvents.find((event) => event.id === selectedEventId),
    [sourceEvents, selectedEventId]
  )

  useEffect(() => {
    if (!open) return

    let cancelled = false

    const loadSourceEvents = async () => {
      setLoadingEvents(true)
      try {
        const response = await api.get<SourceEventsResponse>(
          `/events/${eventSlug}/messaging/email_blasts/source_events`
        )
        if (cancelled) return

        const events = response.events || []
        setSourceEvents(events)
        setSelectedEventId((current) =>
          current && events.some((event) => event.id === current) ? current : events[0]?.id
        )
      } catch (error) {
        if (!cancelled) {
          setSourceEvents([])
          setSelectedEventId(undefined)
          message.error(error instanceof Error ? error.message : 'Failed to load source events.')
        }
      } finally {
        if (!cancelled) setLoadingEvents(false)
      }
    }

    void loadSourceEvents()

    return () => {
      cancelled = true
    }
  }, [open, eventSlug])

  useEffect(() => {
    if (!open || !selectedEventId) {
      setSourceTemplates([])
      setSelectedTemplateKey(undefined)
      return
    }

    let cancelled = false

    const loadSourceTemplates = async () => {
      setLoadingTemplates(true)
      try {
        const query = new URLSearchParams({ source_event_id: selectedEventId }).toString()
        const response = await api.get<SourceTemplatesResponse>(
          `/events/${eventSlug}/messaging/email_blasts/source_templates?${query}`
        )
        if (cancelled) return

        const templates = response.templates || []
        setSourceTemplates(templates)
        setSelectedTemplateKey((current) =>
          current && templates.some((template) => toTemplateKey(template) === current)
            ? current
            : templates[0]
            ? toTemplateKey(templates[0])
            : undefined
        )
      } catch (error) {
        if (!cancelled) {
          setSourceTemplates([])
          setSelectedTemplateKey(undefined)
          message.error(error instanceof Error ? error.message : 'Failed to load source emails.')
        }
      } finally {
        if (!cancelled) setLoadingTemplates(false)
      }
    }

    void loadSourceTemplates()

    return () => {
      cancelled = true
    }
  }, [open, selectedEventId, eventSlug])

  const handleClose = () => {
    setSelectedEventId(undefined)
    setSelectedTemplateKey(undefined)
    setSourceTemplates([])
    onClose()
  }

  const handleLoad = async () => {
    if (!selectedEventId || !selectedEvent) {
      message.error('Please select a source event.')
      return
    }

    if (!selectedTemplate) {
      message.error('Please select an email to load.')
      return
    }

    setLoadingApply(true)
    try {
      const query = new URLSearchParams({
        source_event_id: selectedEventId,
        source_type: selectedTemplate.sourceType,
        source_id: selectedTemplate.sourceId,
      }).toString()

      const response = await api.get<SourceTemplateResponse>(
        `/events/${eventSlug}/messaging/email_blasts/source_template?${query}`
      )

      onApply({
        sourceEventId: selectedEventId,
        sourceEventName: selectedEvent.name,
        sourceType: response.sourceType,
        sourceId: response.sourceId,
        sourceLabel: selectedTemplate.label,
        subjectLine: response.subjectLine || '',
        htmlContent: response.htmlContent || '',
        designJson: response.designJson || {},
      })
      handleClose()
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Failed to load source email content.')
    } finally {
      setLoadingApply(false)
    }
  }

  return (
    <AntModal
      open={open}
      onCancel={handleClose}
      title="Load Email Content from Another Event"
      width={640}
      centered
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={handleClose} disabled={loadingApply}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleLoad}
            loading={loadingApply}
            disabled={!selectedEventId || !selectedTemplate}
          >
            Load Content
          </Button>
        </div>
      }
    >
      <div className="py-2 space-y-4">
        <div>
          <label className="text-sm font-medium text-black/85 mb-2 block">Source Event</label>
          <Select
            value={selectedEventId}
            onChange={(value) => setSelectedEventId(value)}
            loading={loadingEvents}
            options={sourceEvents.map((event) => ({
              value: event.id,
              label: event.name,
            }))}
            placeholder={loadingEvents ? 'Loading events...' : 'Select an event'}
            className="w-full"
            showSearch
            optionFilterProp="label"
            disabled={loadingEvents || sourceEvents.length === 0}
          />
        </div>

        <div>
          <label className="text-sm font-medium text-black/85 mb-2 block">Source Email</label>
          {loadingTemplates ? (
            <div className="h-24 flex items-center justify-center border rounded-lg bg-gray-50">
              <Spin />
            </div>
          ) : (
            <Select
              value={selectedTemplateKey}
              onChange={(value) => setSelectedTemplateKey(value)}
              options={sourceTemplates.map((template) => ({
                value: toTemplateKey(template),
                label: `${template.label} (${template.sourceType === 'manual' ? 'Manual' : 'Automated'})`,
              }))}
              placeholder={
                selectedEventId ? 'Select an email' : 'Select a source event first'
              }
              className="w-full"
              showSearch
              optionFilterProp="label"
              disabled={!selectedEventId || sourceTemplates.length === 0}
            />
          )}
          {!loadingTemplates && selectedEventId && sourceTemplates.length === 0 && (
            <p className="text-xs text-black/45 mt-2">
              No source emails found in this event.
            </p>
          )}
        </div>

        {selectedTemplate?.subjectLine && (
          <div className="rounded-lg border bg-gray-50 px-3 py-2">
            <p className="text-xs text-black/45 mb-1">Subject Preview</p>
            <p className="text-sm text-black/85">{selectedTemplate.subjectLine}</p>
          </div>
        )}
      </div>
    </AntModal>
  )
}

export default LoadEmailContentModal
